import { useCallback, useMemo, useRef } from 'react'

import { f0FormField } from '@factorialco/f0-react'
import { TrainingRequestDetailFragment } from 'generated/resources/graphql'
import { z } from 'zod'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { multiEmployeeSelectorV2FormField } from 'modules/employees/components/EmployeeSelectorV2/formField'

import { useUpdateTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useUpdateTrainingRequest'

type UseFromCatalogFormParams = {
  trainingRequest: TrainingRequestDetailFragment
  onSuccess?: () => void
}

export const useFromCatalogForm = ({ trainingRequest, onSuccess }: UseFromCatalogFormParams) => {
  const { update, loading: updating } = useUpdateTrainingRequest()
  const sendForApprovalRef = useRef(false)

  const setSendForApproval = useCallback((value: boolean) => {
    sendForApprovalRef.current = value
  }, [])

  const participantsField = useMemo(
    () =>
      multiEmployeeSelectorV2FormField({
        label: i18n.t('tasks.approvals.trainings_training_request.fields.participants'),
        section: 'participants',
        mandatory: false,
        fieldConfig: {
          options: { asList: true, size: 'md' },
        },
      }),
    []
  )

  const schema = z.object({
    trainingNeed: f0FormField(
      z
        .object({
          value: z.string().nullable().optional(),
          mentionIds: z.array(z.number()).optional(),
        })
        .optional(),
      {
        label: i18n.t('trainings.training_requests.my_requests.detail.training_need'),
        section: 'basic-info',
        fieldType: 'richtext',
        height: 'sm',
        plainHtmlMode: true,
      }
    ),
    participants: participantsField,
    comments: f0FormField(
      z
        .object({
          value: z.string().nullable().optional(),
          mentionIds: z.array(z.number()).optional(),
        })
        .optional(),
      {
        label: i18n.t('trainings.training_requests.my_requests.detail.comments'),
        section: 'additional-info',
        fieldType: 'richtext',
        height: 'sm',
        plainHtmlMode: true,
      }
    ),
  })

  type FormValues = z.infer<typeof schema>

  const defaultValues: Partial<FormValues> = {
    trainingNeed: { value: trainingRequest.trainingNeed ?? '' },
    participants: {
      type: 'manual',
      ids: (trainingRequest.employeeIds ?? []).map(String),
    },
    comments: { value: trainingRequest.comments ?? '' },
  }

  const sections = {
    'basic-info': {
      title: i18n.t('trainings.training_requests.my_requests.detail.basic_information'),
    },
    participants: {
      title: i18n.t('tasks.approvals.trainings_training_request.fields.participants'),
    },
    'additional-info': {
      title: i18n.t('trainings.training_requests.my_requests.detail.additional_information'),
    },
  }

  const onSubmit = async (values: FormValues) => {
    const sendForApproval = sendForApprovalRef.current
    const employeeIds =
      values.participants?.type === 'manual'
        ? values.participants.ids.map(String)
        : (trainingRequest.employeeIds ?? [])

    const result = await update({
      id: trainingRequest.id,
      trainingName: trainingRequest.trainingName,
      trainingNeed: values.trainingNeed?.value ?? undefined,
      comments: values.comments?.value ?? undefined,
      competencyIds: trainingRequest.competencyIds ?? [],
      employeeIds,
      status: sendForApproval ? 'review' : trainingRequest.status,
      subsidized: trainingRequest.subsidized,
      cost: trainingRequest.cost != null ? Number(trainingRequest.cost) : undefined,
      duration: trainingRequest.duration != null ? Number(trainingRequest.duration) : undefined,
      startDate: trainingRequest.startDate,
      endDate: trainingRequest.endDate,
      schedule: trainingRequest.schedule ?? undefined,
      externalReference: trainingRequest.externalReference,
      providerName: trainingRequest.providerName,
      trainingId: trainingRequest.training?.id,
    })

    const handler = handleResult(result, () => undefined)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onSuccess?.()
      return { success: true as const }
    }

    return { success: false as const }
  }

  return {
    schema,
    defaultValues,
    sections,
    onSubmit,
    updating,
    setSendForApproval,
  }
}
